import { createSolaView, SolaRouter, type SolaView } from '@hm-spt/sola';

import './styles/tokens.css';
import './styles/base.css';
import './styles/app.css';
import './styles/auth.css';
import './styles/deal.css';
import './styles/inventory.css';
import './styles/issue.css';
import './styles/company.css';
import './styles/sourcing.css';
import './styles/server-config.css';

import { AppShell } from './AppShell';
import { DashboardView } from './pages/DashboardView';
import { InventoryView } from './pages/InventoryView';
import { SafetyStockListView } from './pages/SafetyStockListView';
import { LogisticsView } from './pages/LogisticsView';
import { ZonesPreviewView } from './pages/ZonesPreviewView';
import { InventoryCreateView } from './pages/InventoryCreateView';
import { CompanyListView } from './pages/CompanyListView';
import { PendingPartsView } from './pages/PendingPartsView';
import { CatalogEntryView } from './pages/CatalogEntryView';
import { DescriptionRulesView } from './pages/DescriptionRulesView';
import { CatalogRequestsView, toRequestFilter } from './pages/CatalogRequestsView';
import { CatalogRequestDetailView } from './pages/CatalogRequestDetailView';
import { E_CATALOG_REQUEST_FILTERS } from './api/client';
import { BulletinRecordsView } from './pages/BulletinRecordsView';
import { BulletinUnsureView } from './pages/BulletinUnsureView';
import { CatalogView } from './pages/CatalogView';
import { CatalogSearchView } from './pages/CatalogSearchView';
import { LeadListView } from './pages/LeadListView';
import { LeadCreateView } from './pages/LeadCreateView';
import { DealListView } from './pages/DealListView';
import { IssueListView, type IssueListLoaded } from './pages/IssueListView';
import { DealDetailView, type DealDetailLoaded } from './pages/DealDetailView';
import { NeedRfqsView, type NeedRfqsLoaded } from './pages/NeedRfqsView';
import { SourcingView, type SourcingLoaded } from './pages/SourcingView';
import { ServerConfigView, type ServerConfigLoaded } from './pages/ServerConfigView';
import { QuoteEditorView } from './pages/quote/QuoteEditorView';
import * as api from './api/client';
import { SettingsView } from './pages/SettingsView';
import { RentalPresetView } from './pages/RentalPresetView';
import { NotFoundView } from './pages/NotFoundView';
import { MenuStubView } from './pages/MenuStubView';
import { EcountSyncView } from './pages/EcountSyncView';
import { ProductListView } from './pages/ProductListView';
import { MergeCandidatesView } from './pages/MergeCandidatesView';
import { ProductDetailView } from './pages/ProductDetailView';
import { ProductCreateView } from './pages/ProductCreateView';
import { UnitDetailView } from './pages/UnitDetailView';
import { StockDetailView } from './pages/StockDetailView';
import { SalesPreviewListView } from './pages/SalesPreviewListView';
import { SalesOrderListView } from './pages/SalesOrderListView';
import { DirectSaleCreateView, reregisterSourceOrNull } from './pages/DirectSaleCreateView';
import { SalesOrderDetailView } from './pages/SalesOrderDetailView';
import { SalesPreviewDetailView } from './pages/SalesPreviewDetailView';
import { MENU_GROUPS } from './components/menu';
import { RouteErrorView } from './pages/RouteErrorView';
import { LoginView } from './pages/LoginView';
import { ChangePasswordView } from './pages/ChangePasswordView';
import { applyInitialAuthRedirect, isLoggedIn, needsPasswordChange, canManagePricing } from './lib/auth';
import { closeSplitMenusOutside } from './pages/sales-order/actions';

// ─── 첫 로드 시 인증 상태에 맞는 URL 로 자동 redirect ─────────────────
//   - 인증 안 됨 + 보호 페이지   → /login (원래 URL 은 sessionStorage 에 저장)
//   - 비번 변경 필요 + 보호 페이지 → /change-password
//   - /login 인데 이미 로그인됨    → / (또는 /change-password)
applyInitialAuthRedirect();

// ─── 라우트별 가드 wrapper ────────────────────────────────────────────
/**
 * 보호 페이지의 create 함수를 wrap.
 *
 *   - 로그인 안 됨 → LoginView 반환 (URL 은 그대로지만 화면은 로그인)
 *   - 비번 변경 필요 → ChangePasswordView 반환
 *   - 정상 → 원래 페이지
 *
 * 사용:
 *   { pattern: '/',              create: guard(() => new DashboardView()) }
 *   { pattern: '/leads/:id/edit', create: guard(({ id }) => new LeadCreateView({ leadId: +id })) }
 *
 * Sola 가 path params 객체를 create 에 넘김 → 그대로 전달.
 */
/**
 * SolaRouter 의 라우트에 등록되는 View.
 *
 * SolaView<TData extends object> — generic constraint 가 object.
 * 어떤 페이지든 (DashboardView, LeadListView, LoginView, ...) 의 base type.
 *
 * '?'  data interface (LoginData, DashboardData 등) 는 모두 object 호환.
 */
type RouteView = SolaView<object>;

/**
 * 라우트 create 함수의 시그니처.
 *   params  — path params (필수)
 *   query   — URLSearchParams (옵셔널, 거의 안 씀)
 *   loaded  — Route.loader 결과 (loader 있는 라우트만)
 */
type RouteCreateFn<P, T extends RouteView> = (
  params: P,
  query?: URLSearchParams,
  loaded?: unknown,
) => T;

/** 경로 조각이 pending id(양의 정수)인가 — 아니면 옛 탭 이름이라 목록으로 */
function isPendingId(raw: string): boolean {
  return /^[1-9]\d*$/.test(raw);
}

function guard<P, T extends RouteView>(
  createFn: RouteCreateFn<P, T>,
): RouteCreateFn<P, T | LoginView | ChangePasswordView> {
  return (params, query, loaded) => {
    if (!isLoggedIn())           return new LoginView();
    if (needsPasswordChange())   return new ChangePasswordView();
    return createFn(params, query, loaded);
  };
}

/**
 * 역할 게이트 — 허용되지 않으면 그 화면을 **열지 않는다**.
 *
 * `guard` 는 로그인 여부와 비밀번호 변경 여부만 본다. 역할은 안 본다. 그래서
 * 설정 카드를 숨겨도 URL 을 직접 치면 화면이 열렸다(2026-08-20). 카드를
 * 가리는 것만으로는 막은 게 아니다.
 *
 * ★ 이건 UI 게이트다. 진짜 게이트는 백엔드의 requireRole 이고, 여기서 뚫려도
 *   API 가 403 을 낸다. 그래도 막는 이유는 빈 화면에 에러만 뜨는 것보다
 *   애초에 안 보이는 편이 낫기 때문이다.
 *
 * 거부하면 '권한 없음' 화면을 띄우지 않고 설정 홈으로 되돌린다 — 그런 화면은
 * "여기 뭔가 있다"고 알려 주는 셈이다. 안 보이는 게 요구사항이었다.
 * URL 도 replaceState 로 함께 되돌린다. 안 그러면 주소창만 남아 뒤로가기가
 * 같은 자리를 맴돈다.
 */
function roleGate<P, T extends RouteView>(
  allow: () => boolean,
  createFn: RouteCreateFn<P, T>,
  fallbackPath: string,
  fallbackFn: () => RouteView,
): RouteCreateFn<P, T | RouteView> {
  return (params, query, loaded) => {
    if (allow()) return createFn(params, query, loaded);
    history.replaceState(null, '', fallbackPath);
    return fallbackFn();
  };
}

const root = document.getElementById('app');
if (!root) {
  throw new Error('Root element #app not found in index.html');
}

/**
 * 라우터 — Sola 의 SolaRouter 사용.
 *
 * 라우트 정의:
 *   /                  → DashboardView
 *   /preview/…         → 시안 셋 — InventoryView(구매 건 · 실물 · 변동표) · LogisticsView(요청함 · 출고. 구매 목록은 /inventory/receive) · ZonesPreviewView(창고 · 구역). 가짜 데이터
 *   /suppliers         → CompanyListView (거래처 통합 목록 — 고객·매입처)
 *   /catalog           → CatalogSearchView (전체 검색) ★ 2026-08-12
 *   /catalog/_search   → CatalogSearchView (북마크 호환, 같은 뷰)
 *   /catalog/:tableName → CatalogView (특정 테이블)
 *   /settings          → SettingsView (placeholder)
 *   /*                 → NotFoundView
 *
 * TODO(future): /sku/:option_part — SKU 통합 상세 (Phase C)
 * TODO(future): /inventory/new, /inventory/:id — 입고 폼·상세 (Phase B)
 * TODO(future): /suppliers/:id — 공급사 상세 (Phase B)
 */
const router = new SolaRouter([
  // ─── 인증 라우트 (가드 없음 — 비로그인 상태에서 접근) ────────────
  { pattern: '/login',                create: () => new LoginView(),                   title: '로그인 — HCLTech' },
  { pattern: '/change-password',      create: () => new ChangePasswordView(),          title: '비밀번호 변경 — HCLTech' },

  // ─── 보호 라우트 (guard 적용) ───────────────────────────────────
  { pattern: '/',                     create: guard(() => new DealListView()),                title: '딜 — HCLTech' },
  { pattern: '/dashboard',            create: guard(() => new DashboardView()),               title: 'HCLTech 부품 관리자' },
  { pattern: '/deals',                create: guard(() => new DealListView()),                title: '딜 — HCLTech' },
  {
    pattern: '/deal/:id',
    // ADR-0002 loader 패턴 — view 생성 전 필요한 데이터 모두 받아옴.
    // signal 은 라우터가 만든 AbortSignal: 사용자가 도중에 다른 라우트로 가면
    // 자동 취소되어 race / stale 데이터 차단.
    loader: async ({ id }, _q, signal) => {
      const [full, assignees, sourcing, quotes] = await Promise.all([
        api.fetchDealFull(id, { signal }),
        api.fetchActiveAssignees({ signal }).catch(() => []),
        // sourcing/quotes 는 자료 없는 deal 도 빈 배열 반환 — 실패 시 빈 배열
        api.fetchDealSourcing(id, { signal }).catch(() => ({ needs: [] })),
        api.fetchDealQuotes(id, { signal }).catch(() => ({ quotes: [] })),
      ]);
      return { full, assignees, sourcing, quotes };
    },
    create: guard(({ id }, _q, loaded) =>
      new DealDetailView({ dealId: id, ...(loaded as DealDetailLoaded) })),
  },
  {
    // 신규 딜 생성 — 빈 드래프트 딜 위의 딜 상세(블랭크+채팅). loader 는 /deal/:id 와 동일.
    pattern: '/deals/new/:id',
    loader: async ({ id }, _q, signal) => {
      const [full, assignees, sourcing, quotes] = await Promise.all([
        api.fetchDealFull(id, { signal }),
        api.fetchActiveAssignees({ signal }).catch(() => []),
        api.fetchDealSourcing(id, { signal }).catch(() => ({ needs: [] })),
        api.fetchDealQuotes(id, { signal }).catch(() => ({ quotes: [] })),
      ]);
      return { full, assignees, sourcing, quotes };
    },
    create: guard(({ id }, _q, loaded) =>
      new DealDetailView({ dealId: id, newMode: true, ...(loaded as DealDetailLoaded) })),
    title: '신규 딜 생성 — HCLTech',
  },
  {
    // 서버 구성 편집기 (별탭) — 딜 컨텍스트 필수. serverItemId 없으면 신규.
    //   저장 원천은 딜의 deal_item 서브트리 (server_config 테이블 없음).
    pattern: '/deal/:dealId/config/:configId/server/:serverItemId',
    loader: async ({ dealId }, _q, signal) => ({
      full: await api.fetchDealFull(dealId, { signal }),
    }),
    create: guard(({ dealId, configId, serverItemId }, _q, loaded) =>
      new ServerConfigView({
        dealId, configId,
        // 'new' 는 예약어 — 신규 모드로 취급(서버 id 아님).
        ...(serverItemId === 'new' ? {} : { serverItemId }),
        ...(loaded as ServerConfigLoaded),
      })),
    title: '서버 구성 — HCLTech',
  },
  {
    // 견적 편집 지면 페이지 (P1) — ServerConfig 별탭 관례. self-load(onMount)로 /editor 호출.
    pattern: '/deal/:dealId/quote/:quoteId',
    create: guard(({ quoteId }) => new QuoteEditorView({ quoteId })),
    title: '견적 편집 — HCLTech',
  },
  {
    // P9-UI Step 2 — 한 need 의 모든 RFQ batch + supplier 응답 (새 탭)
    pattern: '/need/:id/rfqs',
    loader: async ({ id }, _q, signal) =>
      api.fetchNeedRfqs(id, { signal }),
    create: guard(({ id }, _q, loaded) =>
      new NeedRfqsView({ needId: id, ...(loaded as NeedRfqsLoaded) })),
  },
  { pattern: '/leads',                create: guard(() => new LeadListView()),                title: '리드 — HCLTech' },
  { pattern: '/leads/new',            create: guard(() => new LeadCreateView()),              title: '신규 리드 — HCLTech' },
  { pattern: '/leads/:id/edit',       create: guard(({ id }) => new LeadCreateView({ mode: 'edit', leadId: Number(id) })) },
  // 제품 목록 · 제품 상세 · 제품 등록(실데이터, 2026-10-14 · PRODUCT_SCREENS §1 · §2). 상세(/products/:id)는 새 탭 한 장 — id 는 BIGINT 라 글 그대로
  { pattern: '/products',                   create: guard((_p, query) => new ProductListView({ tab: 'all', search: query ?? new URLSearchParams() })),      title: '제품 목록 — HCLTech' },
  { pattern: '/products/unlinked',          create: guard((_p, query) => new ProductListView({ tab: 'unlinked', search: query ?? new URLSearchParams() })), title: '제품 관리 › 미연결 — HCLTech' },
  { pattern: '/products/merge',             create: guard(() => new MergeCandidatesView()),               title: '제품 관리 › 합칠 후보 — HCLTech' },
  { pattern: '/products/new',               create: guard(() => new ProductCreateView()),                 title: '제품 등록 — HCLTech' },
  { pattern: '/products/:id',               create: guard(({ id }) => new ProductDetailView({ id })),      title: '제품 상세 — HCLTech' },
  // 재고 현황 — ?product_id= 는 제품 상세의 [재고 현황에서 보기](그 product 의 실물만)
  { pattern: '/preview/inventory',          create: guard(() => new InventoryView({ q: '', tab: 'lines', product_id: '', stocktake_id: '', condition: '', from: '', to: '' })), title: '재고 현황 — HCLTech' },
  // 옛 품목별 탭(2026-10-26 퇴역 — 구매 건에 합침). 옛 즐겨찾기가 빈 화면이 되지 않게 구매 건으로
  { pattern: '/preview/inventory/products', create: guard(() => new InventoryView({ q: '', tab: 'lines', product_id: '', stocktake_id: '', condition: '', from: '', to: '' })), title: '재고 현황 — HCLTech' },
  { pattern: '/preview/inventory/flows',    create: guard((_params, query) => new InventoryView({ q: '', tab: 'flows', product_id: '', stocktake_id: '', condition: '', from: query === undefined ? '' : (query.get('from') ?? ''), to: query === undefined ? '' : (query.get('to') ?? '') })), title: '재고 변동표 — HCLTech' },   // ?from=&to= — 일별의 [← 변동표] 가 같은 기간으로
  { pattern: '/preview/inventory/flows/days', create: guard((_params, query) => new InventoryView({ q: '', tab: 'days', product_id: query === undefined ? '' : (query.get('product_id') ?? ''), stocktake_id: '', condition: query === undefined ? '' : (query.get('condition') ?? ''), from: query === undefined ? '' : (query.get('from') ?? ''), to: query === undefined ? '' : (query.get('to') ?? '') })), title: '재고 일별 — HCLTech' },   // 변동표 줄의 [일별] — ?product_id=&condition=(비면 안내만)
  // 옛 일별 주소(2026-10-26 변동표 메뉴 밑으로 옮김) — 옛 즐겨찾기도 같은 페이지로
  { pattern: '/preview/inventory/days',     create: guard((_params, query) => new InventoryView({ q: '', tab: 'days', product_id: query === undefined ? '' : (query.get('product_id') ?? ''), stocktake_id: '', condition: query === undefined ? '' : (query.get('condition') ?? ''), from: query === undefined ? '' : (query.get('from') ?? ''), to: query === undefined ? '' : (query.get('to') ?? '') })), title: '재고 일별 — HCLTech' },   // 변동표 줄의 [일별] — ?product_id=&condition=(비면 안내만)
  { pattern: '/preview/inventory/products/:id', create: guard(({ id }, query) => new StockDetailView({ id, condition: query === undefined ? '' : (query.get('condition') ?? ''), lineKey: query === undefined ? '' : (query.get('line') ?? '') })), title: '재고 상세 — HCLTech' },   // 파트넘버 하나의 재고 흐름(2026-10-26)
  { pattern: '/preview/inventory/units',    create: guard((_params, query) => new InventoryView({ q: '', tab: 'units', product_id: query === undefined ? '' : (query.get('product_id') ?? ''), stocktake_id: '', condition: '', from: '', to: '' })), title: '재고 현황 — HCLTech' },
  { pattern: '/preview/inventory/events',   create: guard((_params, query) => new InventoryView({ q: query === undefined ? '' : (query.get('q') ?? ''), tab: 'events', product_id: query === undefined ? '' : (query.get('product_id') ?? ''), stocktake_id: query === undefined ? '' : (query.get('stocktake_id') ?? ''), condition: '', from: '', to: '' })), title: '재고 변동표 — HCLTech' },
  // 판매 현황 시안(10-28 회의) — 이카운트 판매 임시 표. 상세는 일자-No.에 / 가 들어 ?slip= 질의로
  { pattern: '/sales/new',                  create: guard((_params, query) => new DirectSaleCreateView(reregisterSourceOrNull(query))), title: '판매 등록 — HCLTech' },   // 견적 없는 판매 등록(2026-11-02) · ?from=&items= — 취소한 판매의 품목을 다시 등록
  { pattern: '/sales/:id',                  create: guard(({ id }) => new SalesOrderDetailView(id)),       title: '판매 상세 — HCLTech' },   // 판매 상세 — 품목 고치기 · 추가 · 딜 연결 · 이력(2026-11-03)
  { pattern: '/sales',                      create: guard((_params, query) => new SalesOrderListView(query === undefined ? null : query.get('open'))), title: '판매 현황 — HCLTech' },   // ?open=<id> — 판매 등록에서 돌아와 그 줄을 펼친다
  { pattern: '/preview/sales',              create: guard(() => new SalesPreviewListView()),             title: '판매 현황 (시안) — HCLTech' },
  { pattern: '/preview/sales/detail',       create: guard((_params, query) => new SalesPreviewDetailView({ slipNo: query === undefined ? '' : (query.get('slip') ?? '') })), title: '판매 (시안) — HCLTech' },
  { pattern: '/preview/logistics',          create: guard(() => new LogisticsView({ tab: 'requests' })), title: '물류 — 요청함 (시안) — HCLTech' },
  { pattern: '/inventory/receive',          create: guard(() => new LogisticsView({ tab: 'receive' })),  title: '입고 › 구매 목록 (시안) — HCLTech' },
  { pattern: '/preview/logistics/dispatch', create: guard(() => new LogisticsView({ tab: 'dispatch' })), title: '물류 — 출고 (시안) — HCLTech' },
  { pattern: '/preview/zones',              create: guard(() => new ZonesPreviewView()),                 title: '창고 · 구역 (시안) — HCLTech' },
  { pattern: '/inventory/new',        create: guard(() => new InventoryCreateView({ tab: 'server', purchaseOrNull: null })),   title: '입고 › 재고 등록 — HCLTech' },
  { pattern: '/inventory/new/:tab',   create: guard(({ tab }) => new InventoryCreateView({ tab, purchaseOrNull: null })),      title: '입고 › 재고 등록 — HCLTech' },
  // 구매 목록 탭의 [입고 등록] 이 여는 길 — 그 구매 건의 줄들이 구매 품목 카드로 깔린 채 (현민 2026-10-08 "루트를 하나 더")
  { pattern: '/inventory/new/server/purchase/:po', create: guard(({ po }) => new InventoryCreateView({ tab: 'server', purchaseOrNull: po })), title: '입고 › 재고 등록 — HCLTech' },
  { pattern: '/inventory/units/:id',  create: guard(({ id }) => new UnitDetailView({ id })),                                   title: '실물 상세 — HCLTech' },
  { pattern: '/inventory/stocktake', create: guard(() => new EcountSyncView()),                       title: '실사 · 조정 — HCLTech' },
  { pattern: '/inventory/safety-stock', create: guard(() => new SafetyStockListView()),               title: '필수 재고 — HCLTech' },
  // 메뉴 묶음(제품 · 재고)의 자리 페이지 — 화면이 아직 없는 탭은 그 주소에 자리 페이지가 선다 (현민 2026-10-07 · 08)
  ...MENU_GROUPS.flatMap((group) => group.items.flatMap((item) => item.tabs.filter((tab) => tab.stub).map((tab) => ({ pattern: tab.href, create: guard(() => new MenuStubView({ href: tab.href })), title: `${item.label} › ${tab.label} — HCLTech` })))),
  { pattern: '/suppliers',            create: guard(() => new CompanyListView()),             title: '거래처 — HCLTech' },
  {
    pattern: '/issues',
    loader: async (_p, _q, signal) => {
      const [list, me] = await Promise.all([
        api.fetchIssueList({ signal }),
        api.fetchMe({ signal }).catch(() => ({ is_developer: false })),
      ]);
      return { rows: list.rows, isDeveloper: (me as { is_developer?: boolean }).is_developer === true };
    },
    create: guard((_p, _q, loaded) => new IssueListView(loaded as IssueListLoaded)),
    title: '이슈 — HCLTech',
  },
  { pattern: '/catalog-pending',      create: guard(() => new PendingPartsView()),            title: '임시등록 품목 — HCLTech' },
  // next 메뉴 — 📋 제품 등록. 구역 셋(bulletin · requests · edit), 기본 bulletin
  // 요청함 — 탭 셋은 고정 경로(먼저), 그 밖의 한 조각은 상세(pendingId). 제품 목록의 /products/unlinked · /products/:id 꼴
  { pattern: '/catalog-entry/requests',         create: guard(() => new CatalogRequestsView({ filter: 'drafted' })),                 title: '카탈로그 요청 — HCLTech' },
  ...E_CATALOG_REQUEST_FILTERS.map((filter) => ({ pattern: `/catalog-entry/requests/${filter}`, create: guard(() => new CatalogRequestsView({ filter: toRequestFilter(filter) })), title: '카탈로그 요청 — HCLTech' })),
  // 상세는 양의 정수만 — 아니면(옛 탭 이름 done 같은 북마크) 요청함 기본 탭. NaN 이 API 로 가 400 이 났다(2026-10-14 밤)
  { pattern: '/catalog-entry/requests/:pendingId', create: guard(({ pendingId }) => (isPendingId(pendingId) ? new CatalogRequestDetailView({ pendingId: Number(pendingId) }) : new CatalogRequestsView({ filter: 'drafted' }))), title: '카탈로그 요청 상세 — HCLTech' },
  { pattern: '/catalog-entry/rules',    create: guard(() => new DescriptionRulesView()), title: '카탈로그 규칙 — HCLTech' },
  { pattern: '/catalog-entry',          create: guard(() => new CatalogEntryView({ section: 'bulletin' })), title: '카탈로그 관리 › 불레틴 — HCLTech' },
  { pattern: '/catalog-entry/bulletin', create: guard(() => new CatalogEntryView({ section: 'bulletin' })), title: '카탈로그 관리 › 불레틴 — HCLTech' },
  { pattern: '/catalog-entry/bulletin/:vendor/:doc_no/records', create: guard(({ vendor, doc_no }) => new BulletinRecordsView({ vendor, doc_no: Number(doc_no) })), title: '불레틴 레코드 — HCLTech' },
  { pattern: '/catalog-entry/bulletin/:vendor/:doc_no/unsure',  create: guard(({ vendor, doc_no }) => new BulletinUnsureView({ vendor, doc_no: Number(doc_no) })),  title: '불레틴 미확정 — HCLTech' },
  {
    // 전사 소싱(니즈) 목록 — loader 로 fetchSourcingList
    pattern: '/sourcing',
    loader: async (_p, _q, signal) => api.fetchSourcingList({ signal }),
    create: guard((_p, _q, loaded) => new SourcingView(loaded as SourcingLoaded)),
    title: '소싱 — HCLTech',
  },
  // ★ /catalog 는 **전체 검색**이다 (2026-08-12 현민).
  //   품목을 찾을 때 테이블부터 고르는 것은 어느 테이블에 있는지 이미 알 때만
  //   되는 흐름이다. 파트넘버 하나로 들어오는 게 실제 사용 패턴이라
  //   사이드바의 '카탈로그' 가 곧바로 검색 화면이 되게 한다.
  //
  //   사이드바 href 만 _search 로 돌리지 않는다 — 주소를 직접 치거나 예전 링크로
  //   들어온 사람은 여전히 테이블 뷰어로 가서 "사람마다 다른 화면" 이 된다.
  //   라우트 자체를 바꾸고 _search 는 북마크 호환으로 같은 뷰에 남긴다.
  //   개별 테이블 뷰어(/catalog/:tableName)는 그대로다 — 사이드바에서 들어간다.
  { pattern: '/catalog',              create: guard((_params, query) => new CatalogSearchView({ q: query === undefined ? '' : (query.get('q') ?? ''), table: query === undefined ? '' : (query.get('table') ?? ''), pick: query !== undefined && query.get('pick') === '1' })), title: '전체 검색 — 카탈로그 — HCLTech' },   // ?q=PN&table=표 — 그 행의 드로어를 바로 연다(표를 옮겨도 PN 으로 찾는다)
  { pattern: '/catalog/_search',      create: guard(() => new CatalogSearchView({ q: '', table: '', pick: false })),           title: '전체 검색 — 카탈로그 — HCLTech' },
  { pattern: '/catalog/:tableName',   create: guard(({ tableName }, query) => new CatalogView({ tableName, search: query === undefined ? '' : (query.get('q') ?? '') })) },   // ?q=PN — 제품 상세 "카탈로그에서 보기"(행이 하나면 서랍을 바로 연다)
  // ★ /settings 보다 먼저 둔다 — 선언 순서 first-match-wins 라 뒤에 두면
  //   '/settings' 패턴이 먼저 잡혀 하위 경로가 설정 홈으로 떨어진다.
  //   ★ owner·leader 전용 — 그 외 역할은 설정 홈으로 되돌린다(백엔드도 403).
  { pattern: '/settings/rental-preset', create: guard(roleGate(canManagePricing,
      () => new RentalPresetView(), '/settings', () => new SettingsView())),
      title: '임대 계산식 프리셋 — 설정 — HCLTech' },
  { pattern: '/settings',             create: guard(() => new SettingsView()),                title: '설정 — HCLTech' },
  { pattern: '/*',                    create: guard(() => new NotFoundView()),                title: '페이지 없음 — HCLTech' },
], {
  // ADR-0002 — loader 가 실패하면 라우터가 이 view 를 outlet 에 마운트.
  // 미설정 시 옛 view 가 그대로 유지되어 URL ↔ 화면 어긋남. errorView 권장.
  errorView: (err, ctx) => new RouteErrorView({ error: err, path: ctx.path }),
});

/**
 * AppShell 마운트. AppShell.onMount 안에서 router.attach 호출됨.
 */
const shell = new AppShell({ router });
document.addEventListener('click', closeSplitMenusOutside);   // 판매 버튼 [세팅 완료 ▾] 메뉴 — 바깥을 누르면 닫는다(2026-11-04)
createSolaView(shell, root);
